import { computed } from 'vue';
import { replaceEqualDeep, useQuery } from '@tanstack/vue-query';
import { getCoreAxiosInstance } from '@utils/axios';
import store from '@state/store';
import { queryClient as appClient } from './client';

const api = getCoreAxiosInstance();

// The top bar mounts on the login page too, where a 401 would run logOut.
const loggedIn = () => store.getters['auth/loggedIn'];

export const athleteKeys = {
  names: () => ['athletes', 'names'],
};

// The pickers' row; `item_data` is the label they show and filter on.
export const toAthleteName = (athlete) => ({ ...athlete, item_data: `${athlete.firstName} ${athlete.lastName}` });

// Frozen, so Vue doesn't observe thousands of rows that are only ever replaced.
export const toAthleteNames = (rows) => Object.freeze(rows.map(toAthleteName));

// Most refetches return the same rows; keeping the old frozen list spares every picker a re-render.
// A changed list is taken whole, since replaceEqualDeep's mix of old and new rows isn't frozen.
const keepUnchanged = (old, next) => (replaceEqualDeep(old, next) === old ? old : next);

// Kept in memory only: the rows carry emails and phone numbers, and clinics share machines.
export const athleteNamesQuery = () => ({
  queryKey: athleteKeys.names(),
  queryFn: () => api.get('/athlete/athlete-names').then((res) => toAthleteNames(res.data.athletes.rows)),
  // coming back to the tab after this picks up clients added in another tab or device
  staleTime: 2 * 60 * 1000,
  structuralSharing: keepUnchanged,
});

// For a screen that stays mounted: the cached list, refreshed behind when stale.
export const fetchAthleteNames = (client = appClient) =>
  client.ensureQueryData({ ...athleteNamesQuery(), revalidateIfStale: true });

// [] until the list loads.
export const useAthleteNames = () => {
  const { data } = useQuery({ ...athleteNamesQuery(), enabled: loggedIn });
  return { athletes: computed(() => data.value ?? []) };
};

export const refreshAthleteNames = (client = appClient) =>
  client.invalidateQueries({ queryKey: athleteKeys.names() });

// A saved client shows in every picker at once, in its place or last; then the list refetches.
export const showSavedAthlete = (athlete, client = appClient) => {
  const row = toAthleteName(athlete);
  client.setQueryData(athleteKeys.names(), (list) =>
    list && Object.freeze(list.some((x) => x.id === row.id) ? list.map((x) => (x.id === row.id ? row : x)) : [...list, row])
  );
  return refreshAthleteNames(client);
};
